import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportContext } from '../novelty-check-report-context';
import type { FtoReportState } from '../novelty-check-report-state';
import { Logger } from '@patsnap/tahiti-core';
import { FtoTaskStep } from '@/domains/fto-report/enums';
import { FtoReportRetrievalResultReportModel } from '@/domains/fto-report/models/retrieval-result-report-model';
import { FtoReportAdjustTechFeaturesService } from '../../adjust-tech-features-service';
import { FtoReportTitleService, FtoReportTechFeaturesService } from '../../retrieval-result-report-service';
import { FtoReportTaskStatusModel } from '@/domains/fto-report/models/task-status-model';
import type { FtoReportTaskStatus } from '@/domains/fto-report/types';

@injectable()
export class FtoReportTechFeaturePullSuccessState implements FtoReportState {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FtoReportTitleService)
        private readonly _noveltyCheckReportTitleService: FtoReportTitleService,
        @inject(FtoReportTechFeaturesService)
        private readonly _techFeaturesService: FtoReportTechFeaturesService,
        @inject(FtoReportAdjustTechFeaturesService)
        private readonly _adjustTechFeaturesService: FtoReportAdjustTechFeaturesService,
        @inject(FtoReportRetrievalResultReportModel)
        private readonly _retrievalResultReportModel: FtoReportRetrievalResultReportModel,
        @inject(FtoReportTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: FtoReportTaskStatusModel,
    ) { }

    getStep(): FtoTaskStep {
        return FtoTaskStep.TECH_FEATURE_PULL_SUCCESS;
    }

    async enter(context: FtoReportContext, pullTaskStatus: () => Promise<FtoReportTaskStatus>): Promise<void> {
        this._logger.info('进入<技术特征识别结束>状态');
        this._switchTaskStep(FtoTaskStep.TECH_FEATURE_PULL_SUCCESS);
        await Promise.all([
            //pullTaskStatus(),
            // 获取新颖性报告名称
            this._noveltyCheckReportTitleService.pullNoveltyCheckReportTitle(),
            // 获取技术特征
            this._techFeaturesService.pullTechFeatures(),
        ]);
    }

    async exit(): Promise<void> {
        // 离开技术特征识别状态的逻辑
        this._logger.info('离开<技术特征识别结束>状态');
    }

    async handleNext(context: FtoReportContext): Promise<void> {
        await this._actionsBeforeNext();
        // 从技术特征识别状态进入确认状态
        const confirmState = context.getState(FtoTaskStep.SEARCH_END);
        await context.transitionTo(confirmState);
    }

    async handlePrevious(context: FtoReportContext): Promise<void> {
        this._switchTaskStep(FtoTaskStep.FTO_SEARCH_INPUT);
        // 返回编辑技术要点状态
        const searchInputState = context.getState(FtoTaskStep.FTO_SEARCH_INPUT);
        await context.transitionTo(searchInputState);
    }

    /**
     * 切换任务步骤
     * @param step 目标步骤
     */
    private _switchTaskStep(step: FtoTaskStep) {
        this._noveltyCheckTaskStatusModel.update(state => {
            state.taskStep = step;
            return state;
        });
    }

    private async _actionsBeforeNext() {
        // 获取已经识别的技术特征
        const techFeatures = await this._getTechFeatures();
        await this._adjustTechFeaturesService.adjust(techFeatures);
    }

    // 获取已经识别的技术特征
    private async _getTechFeatures() {
        const techFeatures = this._retrievalResultReportModel.modelData.techFeatures;
        return techFeatures;
    }
}
